import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TimelinePoint } from '@core/interfaces/analytics.interface';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';

@Component({
  selector: 'app-activity-chart',
  template: `
    <div *ngIf="hasData; else noData">
      <apx-chart
        [series]="series"
        [chart]="chartConfig"
        [xaxis]="xaxis"
        [yaxis]="yaxis"
        [fill]="fill"
        [stroke]="stroke"
        [dataLabels]="dataLabels"
        [tooltip]="tooltip"
        [grid]="grid"
        [colors]="colors"
      ></apx-chart>
    </div>
    <ng-template #noData>
      <div class="no-data-msg">
        <i class="ri-line-chart-line"></i>
        <span>Sin datos de actividad</span>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .no-data-msg {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 340px;
        color: #ccc;
        gap: 10px;
      }
      .no-data-msg i {
        font-size: 48px;
      }
      .no-data-msg span {
        font-size: 14px;
      }
    `,
  ],
})
export class ActivityChartComponent implements OnChanges {
  @Input() participantTimeline: TimelinePoint[] = [];
  @Input() conversationTimeline: TimelinePoint[] = [];

  hasData = false;
  series: ApexAxisChartSeries = [];
  colors = ['#757fef', '#00b69b'];

  chartConfig: ApexChart = {
    type: 'area',
    height: 340,
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: 'inherit',
  };

  fill: ApexFill = {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.35,
      opacityTo: 0.02,
      stops: [0, 90, 100],
    },
  };

  stroke: ApexStroke = { curve: 'smooth', width: 2 };
  dataLabels: ApexDataLabels = { enabled: false };
  tooltip: ApexTooltip = {
    shared: true,
    y: { formatter: (val: number) => (val ?? 0).toLocaleString('es') },
  };
  grid: ApexGrid = { borderColor: '#f0f0f0', strokeDashArray: 4 };

  xaxis: ApexXAxis = {
    categories: [],
    labels: { rotate: -35, style: { fontSize: '11px' } },
    tickAmount: 12,
  };

  yaxis: ApexYAxis = {
    labels: {
      formatter: (val: number) =>
        val >= 1000 ? (val / 1000).toFixed(1) + 'k' : String(Math.round(val)),
    },
  };

  ngOnChanges(changes: SimpleChanges): void {
    this.buildChart();
  }

  private buildChart(): void {
    const allDates = new Set([
      ...this.participantTimeline.map((p) => p.date),
      ...this.conversationTimeline.map((c) => c.date),
    ]);
    const categories = Array.from(allDates).sort();

    const pMap = Object.fromEntries(
      this.participantTimeline.map((p) => [p.date, p.count]),
    );
    const cMap = Object.fromEntries(
      this.conversationTimeline.map((c) => [c.date, c.count]),
    );

    this.xaxis = {
      categories,
      labels: { rotate: -35, style: { fontSize: '11px' } },
      tickAmount: Math.min(categories.length, 14),
    };

    this.series = [
      { name: 'Participantes', data: categories.map((d) => pMap[d] || 0) },
      { name: 'Mensajes', data: categories.map((d) => cMap[d] || 0) },
    ];

    this.hasData = categories.length > 0;
  }
}
